// Per-location inventory rollup. Source: GET /api/inventory (parts_inventory
// joined to parts_catalog + locations).
//
// "Pivoted by location" per spec: one row per (item × location). Adjust per
// row opens InventoryAdjustAction pre-seeded with that item + location.
//
// NO SKU COLUMN. This is an operational list: the question it answers is "how
// many of the thing, and where", and the code is not part of that sentence. The
// catalog is the one list that keeps a code column, because it is the item
// master. The SKU is still searchable here, and it is on the item's own page.
//
// The table itself is the shared DataTable (components/table): sorting lives
// there. Text + classification filtering stays HERE, client-side over the
// already-fetched bounded rollup — a test pins that a chip click makes no
// second server round trip.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { type MenuItem } from '../components/ui/Menu';
import Tooltip from '../components/ui/Tooltip';
import { FilterBar, FilterBreak, FilterSearch, FilterSelect } from '../components/FilterBar';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import { DataTable, type DataTableHandle } from '../components/table/DataTable';
import ItemName, { itemLabel } from '../components/ItemName';
import SectionChip from '../components/SectionChip';
import InventoryAdjustAction from './InventoryAdjustAction';
import PrintBoxQrModal from '../Receiving/PrintBoxQrModal';
import { Icon } from '../NavBar/navIcons';
import type {
  InventoryRollupRow,
  LocationRow,
  MaterialType,
  CommodityClass,
} from '../Receiving/types';
import {
  CommodityClassBadge,
  CommodityClassFilterChips,
  MaterialTypeFilterSelect,
} from './classificationDisplay';

const getRowId = (r: InventoryRollupRow) => `${r.part_catalog_id}-${r.location_id}`;

const InventoryPage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const canAdjust = user.isadmin || user.can_admin || user.can_operations;
  // Strict: cost/price visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = !!user.can_financial;
  // Financial-only users open the drawer to edit unit cost; ops/admin to adjust qty.
  const canOpenAdjust = canAdjust || canSeeFinancials;

  const [rows, setRows] = useState<InventoryRollupRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [adjustTarget, setAdjustTarget] = useState<InventoryRollupRow | null>(null);
  const [printQrOpen, setPrintQrOpen] = useState(false);
  // ERP expansion mini-chunk: raw-materials separation. Client-side, same as
  // the text filter below — the rollup is already bounded and fully fetched.
  const [commodityFilter, setCommodityFilter] = useState<'' | CommodityClass>('');
  const [materialFilter, setMaterialFilter] = useState<'' | MaterialType>('');
  // Export CSV writes what the table is holding, so it asks the table rather
  // than re-deriving the rows here.
  const tableRef = useRef<DataTableHandle>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (locationFilter) params.set('location_id', locationFilter);
      const data = (await api.get(
        `/inventory${params.toString() ? '?' + params.toString() : ''}`
      )) as InventoryRollupRow[];
      setRows(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter]);

  useEffect(() => {
    (async () => {
      try {
        const locs = (await api.get('/locations')) as LocationRow[];
        setLocations(locs || []);
      } catch {
        // Not fatal.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Client-side text + classification filters — keeps the server query simple.
  // The
  // inventory rollup is bounded (one row per SKU×location), so client filter
  // is fast.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (commodityFilter && r.commodity_class !== commodityFilter) return false;
      if (materialFilter && r.material_type !== materialFilter) return false;
      if (!q) return true;
      return (
        // Name first here too: it is what somebody types now that it is what
        // the row shows. The code and the description still match, because a
        // person holding a box reads the label, not the screen.
        (r.base_name || '').toLowerCase().includes(q) ||
        (r.display_name || '').toLowerCase().includes(q) ||
        (r.sku || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.mpn || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, commodityFilter, materialFilter]);

  // Warehouses and the rest first; each warehouse is followed by its areas, and each area by its sections.
  // The server includes descendants for a warehouse or area id, so choosing a parent shows everything beneath.
  const locationOptions = useMemo(() => {
    const IND = '\u00A0\u00A0\u00A0';
    const childrenOf = new Map<number, LocationRow[]>();
    const top: LocationRow[] = [];
    for (const l of locations) {
      if ((l.type === 'area' || l.type === 'section') && l.parent_location_id != null) {
        const list = childrenOf.get(l.parent_location_id) ?? [];
        list.push(l);
        childrenOf.set(l.parent_location_id, list);
      } else top.push(l);
    }
    const out: { id: number; label: string }[] = [];
    const walk = (l: LocationRow, depth: number) => {
      out.push({ id: l.id, label: `${IND.repeat(depth)}${l.type === 'section' && l.code ? l.code : l.name}` });
      (childrenOf.get(l.id) ?? []).forEach((c) => walk(c, depth + 1));
    };
    top.forEach((l) => walk(l, 0));
    return out;
  }, [locations]);

  const total = useMemo(() => filtered.reduce((s, r) => s + (r.quantity || 0), 0), [filtered]);

  // Stable via useMemo: fresh column identities would remount every cell
  // (the DataTable contract). setAdjustTarget is a setter, so canOpenAdjust
  // is the only real dependency.
  const columns = useMemo<ColumnDef<InventoryRollupRow, any>[]>(
    () => [
      {
        id: 'item',
        header: 'Item',
        accessorFn: (r: InventoryRollupRow) => itemLabel(r),
        meta: { cellClass: 'max-w-md' },
        cell: ({ row }) => (
          <div className="flex items-center gap-1 min-w-0">
            <Link
              to={`/parts-catalog/${row.original.part_catalog_id}`}
              className="min-w-0 hover:underline"
            >
              <ItemName item={row.original} size="sm" />
            </Link>
            <CommodityClassBadge commodityClass={row.original.commodity_class} />
          </div>
        ),
      },
      {
        id: 'location',
        header: 'Location',
        accessorFn: (r: InventoryRollupRow) => r.location_path ?? r.location_name ?? undefined,
        meta: { cellClass: 'text-on-surface-variant' },
        cell: ({ row }) => {
          const r = row.original;
          if (r.section_code) {
            const where = r.warehouse_name || r.location_name;
            return (
              <Tooltip label={r.location_path || ''}>
                <span className="inline-flex items-center gap-2">
                  <SectionChip code={r.section_code} />
                  {where && <span>{where}</span>}
                </span>
              </Tooltip>
            );
          }
          return r.location_path || r.location_name || '—';
        },
      },
      {
        id: 'quantity',
        header: 'Quantity',
        accessorFn: (r: InventoryRollupRow) => r.quantity,
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
        cell: ({ row }) => row.original.quantity,
      },
      {
        id: 'unit_cost',
        header: 'Unit cost',
        accessorFn: (r: InventoryRollupRow) =>
          r.unit_cost === null ? undefined : parseFloat(r.unit_cost),
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
        cell: ({ row }) =>
          row.original.unit_cost ? `$${parseFloat(row.original.unit_cost).toFixed(2)}` : '—',
      },
      {
        id: 'action',
        header: 'Action',
        meta: { align: 'right' },
        cell: ({ row }) =>
          canOpenAdjust ? (
            <button
              type="button"
              onClick={() => setAdjustTarget(row.original)}
              // The location stays in the name: the same item appears once per
              // place it sits, so the item alone does not identify the row.
              aria-label={`Adjust ${itemLabel(row.original)} at ${row.original.location_name || 'unknown location'}`}
              className={buttonClass('quiet', 'sm')}
            >
              Adjust
            </button>
          ) : (
            <span className="text-xs text-on-surface-disabled">—</span>
          ),
      },
    ],
    [canOpenAdjust]
  );

  const columnVisibility = useMemo(
    () => ({ unit_cost: canSeeFinancials }),
    [canSeeFinancials]
  );

  // The one tool this page has. The hint names what the file will hold before
  // it is asked for: the filters above are the whole difference between an
  // export somebody wanted and a dump of everything.
  const tools: MenuItem[] = [
    {
      label: 'Export CSV',
      hint: `${filtered.length} ${filtered.length === 1 ? 'row' : 'rows'}, current filters`,
      disabled: loading || filtered.length === 0,
      onSelect: () => {
        // null: the table was still loading and wrote nothing.
        const n = tableRef.current?.exportCsv('inventory');
        if (n == null) {
          toast.error('The list is still loading. Try again in a moment.');
          return;
        }
        toast.success(`Exported ${n} row${n === 1 ? '' : 's'}.`);
      },
    },
  ];

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Inventory"
        helpTitle="About inventory"
        lede={
          <>
            Live stock on hand, by location. The master list of parts is the{' '}
            <Link to="/parts-catalog" className="text-primary hover:underline">Catalog</Link>.
          </>
        }
        help={
          <>
            <p>
              This is how much of each part you have right now, and where. One row per part
              per location, so the same SKU appears more than once if it is in more than one
              place.
            </p>
            <p className="text-on-surface font-medium">Where the numbers come from</p>
            <p>
              Nothing here is typed in directly. Quantities move when a box is received,
              when stock is consumed, or when somebody makes a deliberate adjustment. Every
              one of those leaves a record, so any number on this page can be traced back
              to what caused it.
            </p>
            <p className="text-on-surface font-medium">Not everything you own is buildable</p>
            <p>
              Stock held at a vendor, typically a contract assembler you drop-shipped parts
              to, is still yours and still shows here. It does{' '}
              <span className="text-on-surface">not</span> count toward what you can build,
              and it is left out of reorder suggestions, because nobody at your bench can
              pick it up.
            </p>
            <p className="text-on-surface font-medium">Adjusting a count</p>
            <p>
              If a physical count disagrees with the screen, adjust it here rather than
              inventing a receipt. An adjustment says "the number was wrong"; a receipt
              says "a delivery arrived", and only one of those is true.
            </p>
          </>
        }
        actions={
          <>
            <Tooltip label="Scan a part's sticker to see where it is and where it came from">
              <SecondaryAction to="/parts/lookup">
                <Icon name="scan" className="h-4 w-4" />
                Scan Part
              </SecondaryAction>
            </Tooltip>
            <SecondaryAction onClick={() => setPrintQrOpen(true)}>Print QR by Box ID</SecondaryAction>
          </>
        }
        tools={tools}
      />
      }
    >

      <FilterBar>
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search inventory by name, SKU or MPN"
          placeholder="Search name, SKU, MPN…"
        />
        <FilterSelect
          hideLabel
          label="Filter by location"
          value={locationFilter}
          onChange={setLocationFilter}
        >
          <option value="">All locations</option>
          {locationOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
        <FilterBreak />
        <CommodityClassFilterChips value={commodityFilter} onChange={setCommodityFilter} />
        <MaterialTypeFilterSelect
          id="inventory-material-type"
          value={materialFilter}
          onChange={setMaterialFilter}
        />
      </FilterBar>

      {/* The vessel is the DataTable's — no page wrapper. The roll-up sits in
          the footer, under the column it totals rather than above the title. */}
      <DataTable<InventoryRollupRow>
        ref={tableRef}
        data={filtered}
        columns={columns}
        getRowId={getRowId}
        sortable
        stickyHeader
        initialSorting={[{ id: 'item', desc: false }]}
        columnVisibility={columnVisibility}
        footer={
          <span className="text-xs text-on-surface-variant tabular-nums">
            {filtered.length} rows · {total} units
          </span>
        }
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading" className="space-y-2 py-6">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </SkeletonGroup>
        }
        empty={
          <EmptyState variant="area">No rows match the current filter.</EmptyState>
        }
      />

      {adjustTarget && (
        <InventoryAdjustAction
          partCatalogId={adjustTarget.part_catalog_id}
          sku={adjustTarget.sku || ''}
          item={adjustTarget}
          locationId={adjustTarget.location_id}
          locationName={adjustTarget.location_name}
          unitCost={adjustTarget.unit_cost}
          onClose={() => setAdjustTarget(null)}
          onAdjusted={() => {
            setAdjustTarget(null);
            fetchRows();
          }}
          onPriceChanged={() => fetchRows()}
        />
      )}

      {printQrOpen && <PrintBoxQrModal onClose={() => setPrintQrOpen(false)} />}
    </PageLayout>
  );
};

export default InventoryPage;
